import { Lookups } from '@precert/validation';
import axios from 'axios';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import urlJoin from 'url-join';

import { useConfig } from '../../../../_components/config';
import { useFormFieldContext } from '../../../../_components/form-hook';
import { useToastr } from '../../../../_components/toastr';

import type { AttachmentFormData } from './step-clinical-documents';

export interface GetUploadUrlRequest {
  contentType: string;
  contentLength: number;
  fileName: string;
  file: File;
}
export interface GetUploadUrlResponse {
  url: string;
  key: string;
  fileName?: string;
}

interface FileExtensionResults {
  file: File;
  extensionOk: boolean;
  errorMessage: string | null;
}

export function useUploadFile() {
  const { getValues } = useFormContext();
  const { api } = useConfig();
  const { setValue } = useFormFieldContext<AttachmentFormData[]>('attachments');
  const { mutateAsync: getUploadUrl } = useMutation({
    mutationFn: async (file: GetUploadUrlRequest) => {
      const formData = new FormData();
      formData.append('contentType', file.contentType);
      formData.append('contentLength', file.contentLength.toString());
      formData.append('fileName', file.fileName);
      formData.append('file', file.file);

      return axios({
        url: urlJoin(api, 'getUploadUrl'),
        method: 'post',
        withCredentials: true,
        data: formData,
      });
    },
    onError: (err: string) => {
      toast.error(<span>Could not generate presigned url.</span>, {
        details: err,
      });
    },
  });
  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async ({
      url,
      file,
    }: {
      url: string;
      file: File;
      fileName: string;
    }) =>
      axios({
        url,
        method: 'put',
        headers: {
          'Content-Type': file.type,
        },
        data: file,
      }),
    onSuccess: (data, { fileName }) => {
      toast.success(
        <span>
          File <b>{fileName}</b> uploaded successfully.
        </span>
      );
    },
    onError: (err: string) => {
      toast.error(<span>Failed to upload file.</span>, {
        details: err,
      });
    },
  });
  const toast = useToastr();

  // TODO CCRA-37242
  const onDrop = useCallback(
    async (files: File[]) => {
      const attachments: AttachmentFormData[] = getValues('attachments') ?? [];
      const dupes = files.filter((file) =>
        attachments.some(
          (att) => att.fileName === file.name && att.contentLength === file.size
        )
      );
      if (dupes.length > 0) {
        toast.error(
          <span>
            Upload failed: Cannot upload duplicate files.
            <ul>
              {dupes.map((dupe) => (
                <li>{dupe.name}</li>
              ))}
            </ul>
          </span>,
          {
            details: (
              <ul>
                {dupes.map((dupe) => (
                  <li>{dupe.name}</li>
                ))}
              </ul>
            ),
          }
        );
      } else {
        const attachments: AttachmentFormData[] =
          getValues('attachments') ?? [];

        // Validate file extensions before uploading
        const fileExtensionResults: FileExtensionResults[] = files.map(
          (file) => {
            const extensionOk = Lookups.fileTypes.some((e) =>
              file.name.toLowerCase().endsWith(`.${e.toLowerCase()}`)
            );
            const errorMessage = extensionOk
              ? null
              : 'File extension is not accepted';
            return { file, extensionOk, errorMessage };
          }
        );

        // Report any extension failures
        fileExtensionResults
          .filter((f) => f.extensionOk === false)
          // eslint-disable-next-line unicorn/no-array-for-each
          .forEach((failed) => {
            const {
              errorMessage,
              file: { name },
            } = failed;
            // Message matches what is shown if rejected by useDropzone
            // (see step-clinical-documents.tsx)
            toast.error(
              <span>
                <b>Error uploading {name}</b>
                <br />
                {errorMessage}
              </span>
            );
          });

        const promises = fileExtensionResults
          .filter((e) => e.extensionOk)
          .map(async (result) => {
            const {
              type: contentType,
              size: contentLength,
              name,
            } = result.file;

            const response = await getUploadUrl({
              contentType,
              contentLength,
              fileName: name,
              file: result.file,
            });
            if (response.data?.error) {
              toast.error(
                <span>
                  Upload failed for {name}: {response.data.error}
                </span>
              );
              return {
                attachmentId: null,
                contentLength,
                contentType,
                fileName: name,
                uploaded: false,
              };
            }
            const {
              url,
              attachmentId,
              fileName: serverFileName,
            } = response.data;
            const fileName = serverFileName ?? name;
            await uploadFile({ url, file: result.file, fileName });

            const newValue = {
              attachmentId,
              contentLength,
              contentType,
              fileName,
              uploaded: true,
            };
            const currentAttachments: AttachmentFormData[] =
              getValues('attachments') ?? [];
            setValue(
              currentAttachments.map((f) => {
                return f.fileName === name && f.contentLength === contentLength
                  ? newValue
                  : f;
              }),
              { shouldValidate: true }
            );
            return newValue;
          });
        try {
          const uploadedAttachments = await Promise.all(promises);
          const uploadedFiles = uploadedAttachments.filter(
            (e) => e.uploaded === true
          );
          const newFileList = [...attachments, ...uploadedFiles];
          setValue(newFileList, { shouldValidate: true });
        } catch {
          const attachments: AttachmentFormData[] =
            getValues('attachments') || [];
          const newAttachments = attachments.filter((e) => e.uploaded === true);
          setValue(newAttachments, { shouldValidate: true });
        }
      }
    },
    [setValue, uploadFile, getUploadUrl]
  );
  return { onDrop };
}
